import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';

import { concatMap, fromEvent, throttleTime } from 'rxjs';
import { map } from 'rxjs/operators';

import { ERoutes } from 'src/app/core/enums/routes.enum';
import { UserProfile } from 'src/app/core/interfaces/user-profile';

import { AuthService } from 'src/app/core/services/auth.service';
import { TripsService } from 'src/app/core/services/trips.service';
import { SsrHelperService } from 'src/app/core/services/ssr-helper.service';
import { BreakpointService } from 'src/app/core/services/breakpoint.service';
import { MetaTagsConfigService } from 'src/app/core/services/meta-tags-config.service';
import {
  FavoritesProviderService,
  IFavoriteItem,
} from 'src/app/core/services/favorites-provider.service';
import { CloudinaryService } from 'src/app/core/services/cloudinary.service';

import { DeleteTripDialogComponent } from './delete-trip-dialog/delete-trip-dialog.component';
import { SharedDialogComponent } from 'src/app/shared/dialogs/shared-dialog/shared-dialog.component';

import { Config } from '../../../core/configs/config';

@Component({
    selector: 'app-user-trips',
    templateUrl: './user-trips.component.html',
    styleUrls: ['./user-trips.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UserTripsComponent implements OnDestroy {
  public bannerBgUrl = `${Config.imgCdnUrl}images/saved-trips_banner.png`;
  public readonly ERoutes = ERoutes;
  public trips: any[] = [];
  public userProfile: UserProfile;
  public favourites: IFavoriteItem[] = [];
  public isFetching: boolean = false;
  public visibleCount: number = 6;

  @ViewChild('savedTripsContent') savedTripsContent: ElementRef;

  constructor(
    public authService: AuthService,
    public breakpointService: BreakpointService,
    private metaTagsConfigService: MetaTagsConfigService,
    private tripsService: TripsService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private ssrHelperService: SsrHelperService,
    private cloudinaryService: CloudinaryService,
    private favoritesProviderService: FavoritesProviderService,
  ) {
    this.bannerBgUrl = this.cloudinaryService.getCloudImage(
      this.bannerBgUrl,
      true,
    );

    if (this.ssrHelperService.userAgent) {
      this.getUserSavedTrips();
      this.userProfile = this.authService.userProfile;
      this.metaTagsConfigService.setMetaTags(ERoutes.MY_TRIPS);
      fromEvent(window, 'scroll')
        .pipe(takeUntilDestroyed(), throttleTime(50))
        .subscribe(() => {
          const position = window.innerHeight + window.scrollY;
          const { nativeElement } = this.savedTripsContent;
          if (nativeElement && this.trips && this.trips.length) {
            const height =
              nativeElement.offsetHeight - nativeElement.offsetHeight / 3;
            if (position > height) {
              if (this.visibleCount < this.trips.length) {
                this.visibleCount += 3;
                this.cdr.markForCheck();
              }
            }
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.metaTagsConfigService.removeMetaTags();
  }

  public removeTripHandler(trip: any): void {
    const { visible_id, name } = trip;
    this.dialog
      .open(DeleteTripDialogComponent, {
        width: '370px',
        maxWidth: '96vw',
        data: {
          tripName: name,
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.tripsService.removeFromWishList(
            visible_id,
            this.trips,
            this.cdr,
          );
        }
      });
  }

  public openSharedDialog(trip: any): void {
    this.tripsService.getSavedTrip(true, trip.visible_id);
    this.dialog.open(SharedDialogComponent, {
      maxWidth: '96vw',
      maxHeight: '96%',
      data: {
        tripInfo: trip,
      },
    });
  }

  private getUserSavedTrips(): void {
    this.isFetching = true;
    this.tripsService
      .getMyTrips()
      .pipe(
        concatMap((tripsResponse) => {
          return this.favoritesProviderService.getAllFavorites().pipe(
            map((favouritesResponse) => ({
              favouritesResponse,
              tripsResponse,
            })),
          );
        }),
      )
      .subscribe((data) => {
        const { favouritesResponse, tripsResponse } = data;
        if (favouritesResponse?.data?.length) {
          this.favourites = favouritesResponse.data;
        }

        if (tripsResponse && tripsResponse.trips) {
          this.trips = tripsResponse.trips;
        }

        this.isFetching = false;
        this.cdr.markForCheck();
      });
  }
}
